import { FormHelperText, Stack } from '@mui/material';
import { FlowDynamicValueType } from 'flow-schema';
import { type ReactNode, useId, useMemo } from 'react';

import { FormField, api, isDefined } from 'common';

import { BlockSelectField } from '../SelectField';
import { type ConfigFieldProps } from '../types';
import { useSelectedNodeField } from '../useSelectedNodeField';
import { DynamicFieldAutoValue } from './AutoValue';
import { DynamicFieldBlockValue } from './BlockValue';
import { DynamicFieldExpressionValue } from './ExpressionValue';
import { DynamicFieldLiteralValue } from './LiteralValue';
import { type ScriptContextType, getScriptContextKeywords } from './ScriptContext';
import { DynamicFieldTemplateValue } from './TemplateValue';
import { convertDynamicValue, getDynamicValueTypeOptions } from './utils';
import { FeatureFlagKey, useFlag } from '../../../../../../../utils/flags';
import { CodeMirrorAutocompleteProvider } from '../../../../Code/codeMirrorHooks';

import DynamicValue = api.FlowDefinition.DynamicValue;

interface BlockDynamicFieldProps extends ConfigFieldProps {
  description?: string;
  scriptContextType?: ScriptContextType;
  onClear?: () => void;
}

const helperTextByType: Partial<Record<FlowDynamicValueType, ReactNode>> = {
  [FlowDynamicValueType.Auto]: (
    <>
      Enter either a reference enclosed in <code>{'{{ }}'}</code> or a JSON value
    </>
  ),
  [FlowDynamicValueType.Block]: (
    <>
      Select an upstream block whose <code>data</code> will be used
    </>
  ),
  [FlowDynamicValueType.Expression]: (
    <>
      Enter a reference, e.g., <code>patient.data.writtenLanguages</code>
    </>
  ),
  [FlowDynamicValueType.Literal]: (
    <>
      Enter a JSON value, e.g., <code>60</code>, <code>true</code>, or{' '}
      <code>&quot;Pending&quot;</code>
    </>
  ),
  [FlowDynamicValueType.Template]: (
    <>
      Enter text, optionally incorporating references, e.g.,{' '}
      <code>{'Hello, {{patient.data.firstName}}!'}</code>
    </>
  ),
};

/**
 * A field for specifying a value to be dynamically constructed at runtime in
 * any of several ways.
 */
export function BlockDynamicField(props: BlockDynamicFieldProps) {
  const { fieldName, description, scriptContextType, onClear } = props;
  const {
    value = { type: undefined },
    setValue,
    visibleError,
  } = useSelectedNodeField<DynamicValue>(fieldName, { includeChildErrors: true });
  const flowInputAutoTypeOptions = useFlag({ flagKey: FeatureFlagKey.flowInputAutoTypeOptions });
  const typeOptions = getDynamicValueTypeOptions({
    scriptContextType,
    isAutoDynamicValueTypeEnabled: flowInputAutoTypeOptions.isEnabled,
    currentType: value.type,
  });
  const valueFieldId = useId();
  return (
    <Stack gap={1}>
      <FormHelperText>{description}</FormHelperText>
      {isDefined(typeOptions) && (
        <BlockSelectField
          label="Type"
          fieldName={`${fieldName}.type`}
          size="small"
          options={typeOptions.map(({ value, label, helperText }) => ({
            value,
            label: (
              <Stack>
                <span>{label}</span>
                <FormHelperText>{helperText}</FormHelperText>
              </Stack>
            ),
          }))}
          renderValue={(value) =>
            typeOptions.find((option) => option.value === (value as FlowDynamicValueType))?.label
          }
          clearable
          onChange={(event) => {
            const newType = (event.target.value as FlowDynamicValueType | '') || undefined;
            setValue(convertDynamicValue(value, newType));
          }}
          onClear={onClear}
        />
      )}
      {isDefined(value.type) && value.type !== FlowDynamicValueType.Element && (
        <FormField
          id={valueFieldId}
          label="Value"
          helperText={flowInputAutoTypeOptions.isEnabled ? undefined : helperTextByType[value.type]}
          error={visibleError}
        >
          <DynamicValueField
            fieldId={valueFieldId}
            fieldName={fieldName}
            type={value.type}
            scriptContextType={scriptContextType}
          />
        </FormField>
      )}
    </Stack>
  );
}

function DynamicValueField({
  fieldId,
  fieldName,
  type,
  scriptContextType,
}: {
  fieldId: string;
  fieldName: string;
  type: FlowDynamicValueType | undefined;
  scriptContextType?: ScriptContextType;
}) {
  const memoizedAdditionalAutoCompleteLabels = useMemo(
    () => getScriptContextKeywords(scriptContextType),
    [scriptContextType]
  );
  switch (type) {
    case FlowDynamicValueType.Auto:
      return (
        <CodeMirrorAutocompleteProvider
          memoizedAdditionalAutoCompleteLabels={memoizedAdditionalAutoCompleteLabels}
        >
          <DynamicFieldAutoValue fieldId={fieldId} fieldName={fieldName} />
        </CodeMirrorAutocompleteProvider>
      );
    case FlowDynamicValueType.Block:
      return <DynamicFieldBlockValue fieldId={fieldId} fieldName={fieldName} />;
    case FlowDynamicValueType.Expression:
      return (
        <CodeMirrorAutocompleteProvider
          memoizedAdditionalAutoCompleteLabels={memoizedAdditionalAutoCompleteLabels}
        >
          <DynamicFieldExpressionValue fieldId={fieldId} fieldName={fieldName} />
        </CodeMirrorAutocompleteProvider>
      );
    case FlowDynamicValueType.Literal:
      return <DynamicFieldLiteralValue fieldId={fieldId} fieldName={fieldName} />;
    case FlowDynamicValueType.Template:
      return (
        <CodeMirrorAutocompleteProvider
          memoizedAdditionalAutoCompleteLabels={memoizedAdditionalAutoCompleteLabels}
        >
          <DynamicFieldTemplateValue fieldId={fieldId} fieldName={fieldName} />
        </CodeMirrorAutocompleteProvider>
      );
  }
}
